import React, { useMemo } from "react";
import { ChevronDownIcon, SpinnerIcon } from "@/components/icons";
import { DropdownMenu, OptionBadge } from "@/components/ui/DropdownMenu";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { cn } from "@/utils/cn";

export interface DropdownOption<T extends string = string> {
  value: T;
  label: string;
  /** A tag after the label that never truncates away, such as "External": the label shrinks first. */
  badge?: string;
  /** Tailwind classes. When present the trigger renders as a colored pill. */
  bg?: string;
  text?: string;
  dot?: string;
  disabled?: boolean;
}

interface OptionDropdownProps<T extends string> {
  value: T | null;
  options: DropdownOption<T>[];
  /** Omit to render a static, non-interactive value. */
  onChange?: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  align?: "left" | "right";
  /**
   * "badge" (default): inline picker — colored pill when the current option
   * has colors, plain text otherwise. "input": full-width form-field trigger.
   */
  variant?: "badge" | "input";
  /** Menu width class. Defaults to w-36 (badge) / w-full (input). */
  width?: string;
  /** Portal the open menu to document.body — for overflow-hidden hosts
   * (modals) where the menu would otherwise be clipped. */
  portal?: boolean;
  /** Id of the visible label that names the trigger button. */
  ariaLabelledBy?: string;
  /** Names the trigger when no visible label can: a row's picker, repeated down a list. */
  ariaLabel?: string;
  /** The trigger's tooltip: the whole value, for a label the trigger truncates. */
  title?: string;
}

/** An option's text in a trigger: the label, then its badge as its own element. `truncate` ellipsizes the label alone. */
function optionText(option: DropdownOption | undefined, fallback: string, truncate = false) {
  const label = option?.label ?? fallback;
  if (!option?.badge) return truncate ? <span className="truncate">{label}</span> : label;
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate">{label}</span>
      <OptionBadge>{option.badge}</OptionBadge>
    </span>
  );
}

export function OptionDropdown<T extends string>({
  value,
  options,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  align = "left",
  variant = "badge",
  width,
  portal = false,
  ariaLabelledBy,
  ariaLabel,
  title,
}: OptionDropdownProps<T>) {
  const current = options.find((opt) => opt.value === value);
  const colored = variant === "badge" && !!current?.bg;

  const items = useMemo<DropdownMenuEntry[]>(
    () =>
      options.map((opt) => ({
        id: opt.value,
        label: opt.label,
        badge: opt.badge,
        icon: opt.dot ? (
          <div className={`w-2 h-2 rounded-full ${opt.dot}`} />
        ) : undefined,
        onClick: () => onChange?.(opt.value),
        disabled: disabled || loading || opt.disabled || opt.value === value,
      })),
    [options, value, onChange, disabled, loading]
  );

  const dot = loading ? (
    <SpinnerIcon className="w-3 h-3 animate-spin" />
  ) : colored && current?.dot ? (
    <div className={`w-1.5 h-1.5 rounded-full ${current.dot}`} />
  ) : null;

  if (!onChange) {
    if (colored && current) {
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full ${current.bg} ${current.text}`}
        >
          {dot}
          {optionText(current, "")}
        </span>
      );
    }
    return (
      <span className="text-xs font-medium text-gray-700">
        {optionText(current, placeholder)}
      </span>
    );
  }

  const trigger =
    variant === "input" ? (
      <button
        type="button"
        className={cn(
          "w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded-md",
          "focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          current && value ? "text-gray-900" : "text-gray-400",
          (disabled || loading) && "opacity-50 cursor-not-allowed"
        )}
        disabled={disabled}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        title={title}
      >
        {optionText(current, placeholder, true)}
        <ChevronDownIcon className="w-4 h-4 text-gray-400 shrink-0" />
      </button>
    ) : colored && current ? (
      <button
        type="button"
        className={cn(
          `inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors ${current.bg} ${current.text}`,
          (disabled || loading) && "opacity-70"
        )}
      >
        {dot}
        {optionText(current, "")}
        <ChevronDownIcon className="w-3 h-3 opacity-60" />
      </button>
    ) : (
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors",
          (disabled || loading) && "opacity-50"
        )}
      >
        {loading && dot}
        {optionText(current, placeholder)}
        <ChevronDownIcon className="w-3 h-3 opacity-60" />
      </button>
    );

  return (
    <DropdownMenu
      items={items}
      width={width ?? (variant === "input" ? "w-full" : "w-36")}
      align={align}
      menuClassName="rounded-xl"
      className={variant === "input" ? "w-full" : undefined}
      trigger={trigger}
      portal={portal}
    />
  );
}
